import {newSpecPage} from '@stencil/core/testing';
import {Drawer} from '../drawer';

function fireEscapeEvent() {
    const event = new KeyboardEvent('keydown', {code: 'Escape'});
    document.dispatchEvent(event);
}

it('should close drawer and fire custom event for each scenarios: icon, cta, backdrop, keyboard escape and closeDrawer method call', async () => {
    const page = await newSpecPage({
        components: [Drawer],
        html: `
        <joy-drawer id="myDrawer" open confirm-text="Confirm" cancel-text="Cancel">
            <p slot="drawer-header">I am the drawer header</p>
            <p slot="drawer-subheader">I am the drawer subheader</p>
        </joy-drawer>`,
    });

    const spyEmitCancel = jest.spyOn(page.rootInstance.joyCancelDrawer, 'emit');
    expect(page.rootInstance.open).toBe(true);

    // Click on cancel button
    const cancel: HTMLElement = page.root?.shadowRoot?.querySelector('.joy-drawer--cancel')!;
    cancel.click();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(1);

    // Reset
    page.rootInstance.open = true;

    // Click on icon button
    const icon: HTMLElement = page.root?.shadowRoot?.querySelector('.joy-drawer--close')!;
    icon.click();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(2);

    // Reset
    page.rootInstance.open = true;

    // Click on backdrop
    const backdrop: HTMLElement = page.root?.shadowRoot?.querySelector('.joy-drawer__overlay')!;
    backdrop.click();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(3);

    // Reset
    page.rootInstance.open = true;

    // Trigger closeDrawer method
    page.rootInstance.closeDrawer();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(4);

    // Reset
    page.rootInstance.open = true;

    // Keyboard Escape press
    fireEscapeEvent();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(5);
});

it('should not close drawer by clicking on confirm CTA', async () => {
    const page = await newSpecPage({
        components: [Drawer],
        html: `
        <joy-drawer id="myDrawer" open confirm-text="Confirm" cancel-text="Cancel">
          <p slot="drawer-header">I am the drawer header</p>
          <p slot="drawer-subheader">I am the drawer subheader</p>
      </joy-drawer>`,
    });

    const confirm: HTMLElement = page.root?.shadowRoot?.querySelector('.joy-drawer--confirm')!;
    confirm.click();
    expect(page.rootInstance.open).toBe(true);
});

it('should not fire a close event when calling public closeDrawer with specific param value', async () => {
    const page = await newSpecPage({
        components: [Drawer],
        html: `
          <joy-drawer id="myDrawer" open confirm-text="Confirm" cancel-text="Cancel">
            <p slot="drawer-header">I am the drawer header</p>
            <p slot="drawer-subheader">I am the drawer subheader</p>
        </joy-drawer>`,
    });

    const spyEmitCancel = jest.spyOn(page.rootInstance.joyCancelDrawer, 'emit');
    page.rootInstance.closeDrawer({fireEscapeEvent: false});
    expect(spyEmitCancel).toHaveBeenCalledTimes(0);
});

it('should fire a confirm event when clicking on drawer-confirm slot', async () => {
    const page = await newSpecPage({
        components: [Drawer],
        html: `
        <joy-drawer id="myDrawer" open>
            <p slot="drawer-header">I am the drawer header</p>
            <joy-button slot="drawer-confirm">Confirm</joy-button>
        </joy-drawer>`,
    });

    const spyConfirmEvent = jest.spyOn(page.rootInstance.joyConfirmDrawer, 'emit');
    const confirm: HTMLElement = page.body.querySelector('[slot="drawer-confirm"]')!;
    confirm.click();

    expect(spyConfirmEvent).toHaveBeenCalledTimes(1);
});

it('should fire a cancel event with a specific detail source when removing the drawer from the DOM', async () => {
    const page = await newSpecPage({
        components: [Drawer],
        html: `
        <joy-drawer id="myDrawer" open>
            <p slot="drawer-header">I am the drawer header</p>
            <joy-button slot="drawer-confirm">Confirm</joy-button>
        </joy-drawer>`,
    });

    const spyCancel = jest.spyOn(page.rootInstance.joyCancelDrawer, 'emit');
    const drawer: HTMLElement = page.body.querySelector('joy-drawer')!;
    drawer.remove();

    await page.waitForChanges();

    expect(spyCancel).toHaveBeenCalledTimes(1);
    expect(spyCancel).toHaveBeenCalledWith({source: 'disconnectedCallback'});
});

it('should prevent user from scrolling into the page when not using elevated mode', async () => {
    const page = await newSpecPage({
        components: [Drawer],
        html: `
        <joy-drawer id="myDrawer" open>
            <p slot="drawer-header">I am the drawer header</p>
            <joy-button slot="drawer-confirm">Confirm</joy-button>
        </joy-drawer>`,
    });

    const bodyStyle = page.body.style;
    expect(bodyStyle.overflow).toEqual('hidden');
});

it('should allow user keep scrolling into the page when using elevated mode', async () => {
    const page = await newSpecPage({
        components: [Drawer],
        html: `
        <joy-drawer id="myDrawer" elevated open>
            <p slot="drawer-header">I am the drawer header</p>
            <joy-button slot="drawer-confirm">Confirm</joy-button>
        </joy-drawer>`,
    });

    const bodyStyle = page.body.style;
    expect(bodyStyle.overflow).toEqual('');
});
